import { useState, useEffect, useRef } from 'react';
import { auth } from './firebase';
import { getUserProfile } from './userProfile';
import { UserProfile } from './types';
import { captureAttribution } from './attribution';
import { currentMarket } from './market';
import { COUNTRIES, COUNTRY_LIST, foreignNumbers, nationalDigits, phoneProblem, toE164 } from './phone';
import { CountryCode } from './market';
import { clearPrefill, readPrefill } from './prefill';
import {
    CALL_MINUTES_PER_CREDIT,
    MAX_CALL_MINUTES,
    TimeLimitBanner,
    Transcript,
    TranscriptCard,
    TranscriptLine,
    wasCutOffAtTimeLimit,
} from './Transcript';
import { fmtTime, zoneForCountry } from './timeFormat';
import './App.css';

interface CallInfo {
    call_sid: string;
    call_id: string;
    to_number: string;
    status: string;
}

interface StatusUpdate {
    timestamp: string;
    status: string;
    duration?: string;
}

// How the call status shows what picked up.
const answeredByLabel = (answeredBy: string): string =>
    answeredBy === 'machine' ? 'answering machine' : answeredBy === 'fax' ? 'fax machine' : answeredBy;

const defaultPromptText = `Introduce yourself as an assistant to...
Your goal is to...
The bigger context is that... 
Make sure to mention (each item gets said during the call, one at a time):
- ...
- ...
(Optional) I specifically want to be connected to...
(Optional) If they ask for a callback number, give them this number...`;

const exampleText = (callbackNumber: string) => `Introduce yourself as assistant to John Smith who has an appointment for tomorrow 11am.
Ask them if we can reschedule for next month (March). The preference is for the morning.
Accept whatever they suggest as long as it is morning.
Make sure to mention:
- John can't make tomorrow at 11am
- Only morning slots work for him
- Callback number: ${callbackNumber}`;

interface CallCenterProps {
    userProfile: UserProfile | null;
    onProfileUpdate: (profile: UserProfile | null) => void;
}

function CallCenter({ userProfile, onProfileUpdate }: CallCenterProps) {
    const currentUser = auth.currentUser;
    const market = currentMarket();
    // What the visitor typed on the landing page, if they came from there.
    const [prefill] = useState(readPrefill);
    // The country decides the dialing prefix, the number's format and the
    // language the call defaults to; the field itself holds the national number.
    const [country, setCountry] = useState<CountryCode>(prefill?.country ?? market.defaultCountry);
    const [phoneInput, setPhoneInput] = useState(prefill?.phone ?? '');
    const [prompt, setPrompt] = useState(prefill?.prompt ?? defaultPromptText);
    const [language, setLanguage] = useState<'ja' | 'en'>(
        prefill?.language ?? (prefill?.country ? COUNTRIES[prefill.country].defaultLanguage : market.defaultLanguage)
    );
    // The call's length limit: one credit buys CALL_MINUTES_PER_CREDIT;
    // MAX_CALL_MINUTES holds a second credit, charged only if the call runs
    // past the first. So extending needs two credits available.
    const [maxMinutes, setMaxMinutes] = useState<number>(CALL_MINUTES_PER_CREDIT);
    const canExtend = !!userProfile && userProfile.creditsRemaining >= 2;
    useEffect(() => {
        if (!canExtend) setMaxMinutes(CALL_MINUTES_PER_CREDIT);
    }, [canExtend]);
    const [calling, setCalling] = useState(false);
    const [error, setError] = useState('');
    const [callInfo, setCallInfo] = useState<CallInfo | null>(null);
    const [statusUpdates, setStatusUpdates] = useState<StatusUpdate[]>([]);
    // 'machine' once the bot heard an answering-machine beep, 'fax' for a fax.
    const [answeredBy, setAnsweredBy] = useState<string | null>(null);
    const [transcript, setTranscript] = useState<TranscriptLine[]>([]);
    const [phoneError, setPhoneError] = useState('');
    const phoneDigits = nationalDigits(country, phoneInput);

    const changeCountry = (next: CountryCode) => {
        setCountry(next);
        setPhoneError('');
        // Calls to a country are usually made in its language.
        setLanguage(COUNTRIES[next].defaultLanguage);
    };
    const eventSourceRef = useRef<EventSource | null>(null);
    const transcriptEndRef = useRef<HTMLDivElement | null>(null);

    // The prefill is used once; later visits start from the template.
    useEffect(() => {
        if (prefill) clearPrefill();
    }, [prefill]);

    // Cleanup EventSource on unmount
    useEffect(() => {
        return () => {
            if (eventSourceRef.current) {
                eventSourceRef.current.close();
            }
        };
    }, []);

    // Auto-scroll transcript to bottom
    useEffect(() => {
        transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [transcript]);

    const getStatusColor = (status: string): string => {
        switch (status) {
            case 'queued':
                return '#888';
            case 'ringing':
                return '#4285f4';
            case 'in-progress':
            case 'in_progress':
                return '#51cf66';
            case 'completed':
                return '#51cf66';
            case 'no-answer':
            case 'no_answer':
            case 'failed':
            case 'busy':
            case 'canceled':
                return '#ff6b6b';
            case 'answering machine':
            case 'fax machine':
                return '#fbbf24';
            default:
                return '#aaa';
        }
    };

    // timeZone: the zone the call's times are shown in (see zoneForCountry).
    const startCallStream = (callId: string, timeZone: string | undefined) => {
        // Close existing connection if any
        if (eventSourceRef.current) {
            eventSourceRef.current.close();
        }

        // Clear previous updates
        setStatusUpdates([]);
        setTranscript([]);
        setAnsweredBy(null);

        const serverUrl = 'https://call-for-me-prod-3055119120.us-central1.run.app';
        const eventSource = new EventSource(`${serverUrl}/call-stream/${callId}`);
        eventSourceRef.current = eventSource;

        eventSource.onmessage = (event) => {
            console.log('Call stream update:', event.data);

            try {
                const data = JSON.parse(event.data);

                if (data.type === 'connected') {
                    console.log('Connected to call stream');
                    return;
                }

                if (data.type === 'status') {
                    // Handle status update
                    const timestamp = fmtTime(new Date().toISOString(), timeZone);
                    const newUpdate: StatusUpdate = {
                        timestamp,
                        status: data.status,
                        duration: data.duration !== 'N/A' ? data.duration : undefined
                    };

                    setStatusUpdates(prev => [...prev, newUpdate]);

                    // If call is complete, close the connection and re-enable button
                    const terminalStatuses = ['completed', 'no-answer', 'no_answer', 'failed', 'busy', 'canceled'];
                    if (terminalStatuses.includes(data.status)) {
                        eventSource.close();
                        setCalling(false);
                        
                        // Refresh user profile to update credits
                        if (currentUser) {
                            getUserProfile(currentUser.uid).then(updatedProfile => {
                                onProfileUpdate(updatedProfile);
                            });
                        }
                    }
                } else if (data.type === 'answered_by') {
                    // The bot heard an answering-machine beep or a fax tone.
                    setAnsweredBy(data.answered_by);
                    setStatusUpdates(prev => [...prev, {
                        timestamp: fmtTime(new Date().toISOString(), timeZone),
                        status: answeredByLabel(data.answered_by),
                    }]);
                } else if (data.type === 'transcript') {
                    // Handle transcript update
                    const timestamp = fmtTime(data.timestamp, timeZone);
                    const newMessage: TranscriptLine = {
                        timestamp,
                        role: data.role,
                        event: data.event,
                        content: data.content
                    };

                    setTranscript(prev => [...prev, newMessage]);
                }
            } catch (e) {
                console.error('Error parsing call stream update:', e);
            }
        };

        eventSource.onerror = (error) => {
            console.error('SSE error:', error);
            eventSource.close();
            setCalling(false);
            setError('Error receiving call updates. Connection closed.');
        };
    };

    const foreignContact = foreignNumbers(country, prompt);

    const handleCall = async () => {
        setError('');
        setPhoneError('');
        setCallInfo(null);
        setStatusUpdates([]);
        setTranscript([]);
        setAnsweredBy(null);

        // Validation
        if (!prompt.trim()) {
            setError('Please provide a prompt for the call.');
            return;
        }

        const problem = phoneProblem(country, phoneDigits);
        if (problem) {
            setPhoneError(problem);
            return;
        }

        if (!userProfile || userProfile.creditsRemaining <= 0) {
            setError('You have no credits remaining. Please upgrade your plan.');
            return;
        }

        setCalling(true);

        try {
            // Get Firebase auth token
            const token = await currentUser?.getIdToken();

            // First-touch attribution + current path, for per-call analytics
            const attribution = captureAttribution();

            // Make the POST request to the backend
            const response = await fetch('https://call-for-me-prod-3055119120.us-central1.run.app/dialout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    to_number: toE164(country, phoneDigits),
                    // The backend picks the caller ID for the destination country.
                    prompt: prompt,
                    language: language,
                    max_duration_seconds: maxMinutes * 60,
                    attribution: Object.keys(attribution).length > 0 ? attribution : undefined,
                    landing_page: window.location.href
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.detail || errorData.message || `Failed to initiate call: ${response.status}`);
            }

            const data = await response.json();
            console.log('Call response:', data);

            // Store call information
            setCallInfo({
                call_sid: data.call_sid,
                call_id: data.call_id,
                to_number: data.to_number,
                status: data.status
            });

            // Start listening for status and transcript updates using call_id
            startCallStream(data.call_id, zoneForCountry(country));

            // Note: We don't refresh user profile here anymore, 
            // it will be refreshed when the call terminates
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to initiate call');
            console.error('Call error:', err);
            setCalling(false);
        }
    };

    return (
        <div className="App">
            <header className="App-header" style={{ justifyContent: 'flex-start', paddingTop: '80px', padding: '80px 20px 20px 20px' }}>

                {/* Error Messages */}
                {error && (
                    <div style={{
                        color: '#ff6b6b',
                        margin: '0 0 20px 0',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        backgroundColor: 'var(--error-bg)',
                        maxWidth: '600px',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}>
                        {error}
                    </div>
                )}

                {/* Main Form */}
                <div style={{ maxWidth: '600px', width: '100%', padding: '0 4px', boxSizing: 'border-box' }}>
                    {/* Prompt Field */}
                    <div style={{ marginBottom: '20px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontSize: '16px',
                            fontWeight: '500',
                            textAlign: 'left'
                        }}>
                            Prompt (critical for your call to succeed)
                        </label>
                        <textarea
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            style={{
                                width: '100%',
                                minHeight: '280px',
                                padding: '16px',
                                fontSize: '15px',
                                backgroundColor: 'var(--bg-input)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px',
                                color: 'var(--text-primary)',
                                fontFamily: 'inherit',
                                resize: 'vertical',
                                boxSizing: 'border-box'
                            }}
                        />
                        {foreignContact.length > 0 && (
                            <div role="note" style={{
                                marginTop: '8px',
                                padding: '10px 14px',
                                fontSize: '13px',
                                color: 'var(--text-primary)',
                                backgroundColor: 'var(--bg-secondary)',
                                borderLeft: '3px solid #e0a030',
                                borderRadius: '6px',
                                textAlign: 'left'
                            }}>
                                {foreignContact.join(', ')} is not a {COUNTRIES[country].adjective} number.
                                {' '}Businesses often need a local contact number to take a reservation, and staff may not be able to write down or call an overseas one.
                                {' '}If you can, give a local number instead, such as your hotel's or your tour guide's.
                            </div>
                        )}
                        <div style={{
                            marginTop: '8px',
                            fontSize: '12px',
                            color: 'var(--text-muted)',
                            textAlign: 'left',
                            whiteSpace: 'pre-line'
                        }}>
                            <strong>Example:</strong> {exampleText(market.exampleCallbackNumber)}
                        </div>
                    </div>

                    {/* Phone Number Field */}
                    <div style={{ marginBottom: '24px' }}>
                        <label htmlFor="call-phone" style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontSize: '16px',
                            fontWeight: '500',
                            textAlign: 'left'
                        }}>
                            Phone number to call
                        </label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <select
                                aria-label="Country"
                                value={country}
                                onChange={(e) => changeCountry(e.target.value as CountryCode)}
                                style={{
                                    padding: '12px 10px',
                                    fontSize: '15px',
                                    backgroundColor: 'var(--bg-input)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '8px',
                                    color: 'var(--text-primary)',
                                    fontFamily: 'inherit',
                                    flex: '0 0 auto'
                                }}
                            >
                                {COUNTRY_LIST.map((c) => (
                                    <option key={c.code} value={c.code}>
                                        {c.flag} {c.dialCode} {c.label}
                                    </option>
                                ))}
                            </select>
                            <input
                                id="call-phone"
                                type="tel"
                                value={phoneInput}
                                onChange={(e) => {
                                    const input = e.target.value;
                                    setPhoneInput(input);
                                    const digits = nationalDigits(country, input);
                                    // Only complain while typing about things that can't
                                    // become right by typing more.
                                    const problem = phoneProblem(country, digits);
                                    setPhoneError(
                                        problem && (problem.startsWith('Too long') || problem.startsWith('Premium'))
                                            ? problem
                                            : ''
                                    );
                                }}
                                placeholder={COUNTRIES[country].placeholder}
                                style={{
                                    flex: '1 1 auto',
                                    minWidth: 0,
                                    padding: '12px 16px',
                                    fontSize: '15px',
                                    backgroundColor: 'var(--bg-input)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '8px',
                                    color: 'var(--text-primary)',
                                    fontFamily: 'monospace',
                                    boxSizing: 'border-box'
                                }}
                            />
                        </div>
                        <div style={{
                            fontSize: '12px',
                            color: 'var(--text-muted)',
                            marginTop: '6px',
                            textAlign: 'left'
                        }}>
                            {COUNTRIES[country].hint}
                            {phoneDigits && !phoneProblem(country, phoneDigits) && (
                                <> Will dial <strong>{toE164(country, phoneDigits)}</strong>.</>
                            )}
                        </div>
                        {phoneError && (
                            <div style={{
                                fontSize: '13px',
                                color: '#ff6b6b',
                                marginTop: '6px',
                                textAlign: 'left',
                                fontWeight: '500'
                            }}>
                                {phoneError}
                            </div>
                        )}
                    </div>

                    {/* Language Selector */}
                    <div style={{ marginBottom: '24px' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontSize: '16px',
                            fontWeight: '500',
                            textAlign: 'left'
                        }}>
                            The call will be made in...
                        </label>
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            backgroundColor: 'var(--bg-input)',
                            padding: '4px',
                            borderRadius: '8px',
                            border: '1px solid var(--border-color)'
                        }}>
                            <button
                                type="button"
                                aria-pressed={language === 'ja'}
                                onClick={() => setLanguage('ja')}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    fontSize: '15px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    backgroundColor: language === 'ja' ? 'var(--lang-active-bg)' : 'transparent',
                                    color: language === 'ja' ? 'var(--lang-active-text)' : 'var(--lang-inactive-text)',
                                    border: language === 'ja' ? '1px solid var(--lang-active-border)' : '1px solid transparent',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                Japanese
                            </button>
                            <button
                                type="button"
                                aria-pressed={language === 'en'}
                                onClick={() => setLanguage('en')}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    fontSize: '15px',
                                    fontWeight: '500',
                                    cursor: 'pointer',
                                    backgroundColor: language === 'en' ? 'var(--lang-active-bg)' : 'transparent',
                                    color: language === 'en' ? 'var(--lang-active-text)' : 'var(--lang-inactive-text)',
                                    border: language === 'en' ? '1px solid var(--lang-active-border)' : '1px solid transparent',
                                    borderRadius: '6px',
                                    transition: 'all 0.2s'
                                }}
                            >
                                English
                            </button>
                        </div>
                    </div>

                    {/* Maximum call length */}
                    <div style={{ marginBottom: '24px', opacity: canExtend ? 1 : 0.55 }}>
                        <label htmlFor="call-max-length" style={{
                            display: 'block',
                            marginBottom: '8px',
                            fontSize: '16px',
                            fontWeight: '500',
                            textAlign: 'left'
                        }}>
                            Maximum call length: {maxMinutes} min
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{CALL_MINUTES_PER_CREDIT} min</span>
                            <input
                                id="call-max-length"
                                type="range"
                                min={CALL_MINUTES_PER_CREDIT}
                                max={MAX_CALL_MINUTES}
                                step={MAX_CALL_MINUTES - CALL_MINUTES_PER_CREDIT}
                                value={maxMinutes}
                                disabled={!canExtend}
                                onChange={(e) => setMaxMinutes(Number(e.target.value))}
                                style={{
                                    flex: 1,
                                    margin: 0,
                                    accentColor: maxMinutes > CALL_MINUTES_PER_CREDIT ? '#fbbf24' : '#51cf66',
                                    cursor: canExtend ? 'pointer' : 'not-allowed'
                                }}
                            />
                            <span style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>{MAX_CALL_MINUTES} min</span>
                        </div>
                        <div style={{
                            fontSize: '12px',
                            color: 'var(--text-muted)',
                            marginTop: '6px',
                            textAlign: 'left'
                        }}>
                            {canExtend
                                ? `Extends the limit to ${MAX_CALL_MINUTES} minutes for 1 extra credit, charged only if the call runs longer than ${CALL_MINUTES_PER_CREDIT} minutes. Useful for complex matters.`
                                : `Extending to ${MAX_CALL_MINUTES} minutes needs 2 credits available. The extra credit is charged only if the call runs longer than ${CALL_MINUTES_PER_CREDIT} minutes.`}
                        </div>
                    </div>

                    {/* Call Button */}
                    <button
                        onClick={handleCall}
                        disabled={calling || !userProfile || userProfile.creditsRemaining <= 0}
                        style={{
                            width: '100%',
                            padding: '16px',
                            fontSize: '18px',
                            fontWeight: '600',
                            cursor: calling || !userProfile || userProfile.creditsRemaining <= 0 ? 'not-allowed' : 'pointer',
                            backgroundColor: (() => {
                                if (!calling && userProfile && userProfile.creditsRemaining > 0) {
                                    return '#51cf66';
                                }
                                if (calling && statusUpdates.length > 0) {
                                    const currentStatus = statusUpdates[statusUpdates.length - 1].status;
                                    if (currentStatus === 'in-progress' || currentStatus === 'in_progress') {
                                        return '#fbbf24';
                                    }
                                }
                                return '#555';
                            })(),
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            transition: 'background-color 0.2s'
                        }}
                    >
                        {calling && statusUpdates.length > 0 
                            ? statusUpdates[statusUpdates.length - 1].status
                            : calling 
                                ? 'Initiating call...' 
                                : 'Call Now'
                        }
                    </button>

                    {userProfile && userProfile.creditsRemaining <= 0 && (
                        <div style={{
                            marginTop: '12px',
                            fontSize: '14px',
                            color: '#ff6b6b',
                            textAlign: 'center'
                        }}>
                            You have no credits remaining. <a href="/plans" style={{ color: '#4285f4' }}>Buy credits</a>
                        </div>
                    )}
                </div>

                {/* Live Transcript Display - Chat Style */}
                {callInfo && (
                    <TranscriptCard title="Live Transcript">
                        {wasCutOffAtTimeLimit(transcript) && <TimeLimitBanner limitMinutes={maxMinutes} />}
                        <Transcript
                            messages={transcript}
                            empty="Waiting for conversation to start..."
                            endRef={transcriptEndRef}
                        />
                    </TranscriptCard>
                )}

                {/* Call Status Display */}
                {callInfo && (
                    <div style={{
                        margin: '0 0 20px 0',
                        padding: '16px 20px',
                        borderRadius: '8px',
                        backgroundColor: 'var(--bg-card)',
                        border: '1px solid var(--border-color)',
                        maxWidth: '600px',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}>
                        <h3 style={{ 
                            margin: '0 0 12px 0', 
                            fontSize: '16px',
                            fontWeight: '600'
                        }}>
                            Call Status
                        </h3>
                        
                        <div style={{ marginBottom: '16px', fontSize: '14px', color: 'var(--text-secondary)' }}>
                            <div>
                                <strong style={{ color: 'var(--text-primary)' }}>To:</strong> {callInfo.to_number}
                            </div>
                            {answeredBy && (
                                <div>
                                    <strong style={{ color: 'var(--text-primary)' }}>Answered by:</strong>{' '}
                                    {answeredByLabel(answeredBy)}
                                </div>
                            )}
                        </div>

                        {statusUpdates.length > 0 && (
                            <div>
                                <div style={{
                                    fontSize: '13px',
                                    color: 'var(--text-muted)',
                                    marginBottom: '8px',
                                    fontStyle: 'italic'
                                }}>
                                    Status Updates:
                                </div>
                                {statusUpdates.map((update, index) => (
                                    <div
                                        key={index}
                                        style={{
                                            padding: '8px 12px',
                                            marginBottom: '6px',
                                            borderLeft: `3px solid ${getStatusColor(update.status)}`,
                                            backgroundColor: 'var(--status-row-bg)',
                                            borderRadius: '4px',
                                            fontSize: '14px'
                                        }}
                                    >
                                        <span style={{ color: 'var(--text-muted)', marginRight: '12px' }}>
                                            {update.timestamp}
                                        </span>
                                        <span style={{ 
                                            color: getStatusColor(update.status),
                                            fontWeight: '600'
                                        }}>
                                            {update.status}
                                        </span>
                                        {update.duration && (
                                            <span style={{ color: 'var(--text-secondary)', marginLeft: '8px' }}>
                                                (Duration: {update.duration}s)
                                            </span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        {statusUpdates.length === 0 && (
                            <div style={{
                                fontSize: '13px',
                                color: 'var(--text-muted)',
                                fontStyle: 'italic'
                            }}>
                                Listening for status updates...
                            </div>
                        )}
                    </div>
                )}
            </header>
        </div>
    );
}

export default CallCenter;
